<template>
  <div>
    <!-- Page Header -->
    <b-card class="mb-4">
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <h3 class="mb-1">Reporte de Asignación de Cursos</h3>
          <p class="text-muted mb-0">Programación académica del segundo semestre 2025</p>
        </div>

        <!-- Botones de Acción -->
        <div>
          <b-button
            variant="outline-success"
            @click="showExportOptions = !showExportOptions"
            v-b-tooltip.hover
            title="Exportar Reporte"
            class="mr-2"
          >
            <feather-icon icon="DownloadIcon" class="mr-1" />
            Exportar
          </b-button>

          <b-button
            variant="primary"
            @click="showFilters = !showFilters"
            v-b-tooltip.hover
            title="Filtrar Reporte"
          >
            <feather-icon icon="FilterIcon" class="mr-1" />
            Filtrar
          </b-button>
        </div>
      </div>

      <!-- Menú de Exportación -->
      <b-collapse v-model="showExportOptions" class="mt-3">
        <b-card>
          <h6 class="mb-3">Opciones de Exportación</h6>
          <div class="d-flex flex-wrap gap-2">
            <b-button
              variant="outline-danger"
              size="sm"
              @click="exportToPDF"
              :disabled="asignaciones.length === 0"
            >
              <feather-icon icon="FileTextIcon" class="mr-1" />
              Exportar PDF
            </b-button>

            <b-button
              variant="outline-success"
              size="sm"
              @click="exportToExcel"
              :disabled="asignaciones.length === 0"
            >
              <feather-icon icon="FileIcon" class="mr-1" />
              Exportar Excel
            </b-button>
          </div>
        </b-card>
      </b-collapse>

      <!-- Filtros -->
      <b-collapse v-model="showFilters" class="mt-3">
        <b-card>
          <h6 class="mb-3">Filtros del Reporte</h6>
          <b-row>
            <b-col md="3">
              <b-form-group label="Ciclo">
                <b-form-select
                  v-model="filters.ciclo"
                  :options="ciclosOptions"
                  placeholder="Todos los ciclos"
                />
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group label="Sección">
                <b-form-select
                  v-model="filters.seccion"
                  :options="seccionesOptions"
                  placeholder="Todas las secciones"
                />
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group label="Salón">
                <b-form-select
                  v-model="filters.salon"
                  :options="salonesOptions"
                  placeholder="Todos los salones"
                />
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group label="Catedrático">
                <b-form-select
                  v-model="filters.catedratico"
                  :options="catedraticosOptions"
                  placeholder="Todos los catedráticos"
                />
              </b-form-group>
            </b-col>
          </b-row>
          <div class="text-right">
            <b-button variant="outline-secondary" size="sm" @click="clearFilters">
              Limpiar Filtros
            </b-button>
          </div>
        </b-card>
      </b-collapse>
    </b-card>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-4">
      <b-spinner variant="primary" label="Cargando..."></b-spinner>
      <p class="mt-2">Cargando reporte de asignaciones...</p>
    </div>

    <!-- Contenido Principal -->
    <b-card v-else>
      <!-- Header con Logo y Universidad -->
      <div class="header mb-4">
        <div class="logo-section">
          <div class="logo-container">
            <img 
              src="@/assets/images/logo/umg-logo.png" 
              alt="UMG Logo" 
              class="umg-logo"
            >
          </div>
        </div>
        <div class="university-info text-center">
          <h4 class="university-name mb-1">UNIVERSIDAD MARIANO GÁLVEZ DE GUATEMALA</h4>
          <p class="university-center mb-0">Plan Sábado</p>
        </div>
      </div>

      <!-- Información de la Carrera -->
      <div class="career-info mb-4">
        <b-row>
          <b-col md="6">
            <p class="mb-1"><strong>5122/2014</strong></p>
            <p class="mb-1"><strong>Carrera:</strong> Licenciatura en Administración de Empresas</p>
          </b-col>
          <b-col md="6" class="text-right">
            <p class="mb-1"><strong>Programación Segundo Semestre 2025</strong></p>
          </b-col>
        </b-row>
      </div>

      <!-- Tabla de Horarios -->
      <div class="schedule-table">
        <b-table
          :items="horariosAgrupados"
          :fields="tableFields"
          responsive
          striped
          hover
          class="mb-0 schedule-grid-table"
          show-empty
          empty-text="No se encontraron asignaciones con los filtros aplicados"
        >
          <template #cell(horario)="row">
            <div class="time-slot">
              <strong>{{ row.item.horario }}</strong>
            </div>
          </template>

          <template #cell(seccionA)="row">
            <div v-if="row.item.seccionA" class="combined-info">
              <div class="course-section">
                <div class="course-code">{{ row.item.seccionA.codigoCurso }}</div>
                <div class="course-name">{{ row.item.seccionA.nombreCurso }}</div>
              </div>
              <div class="teacher-section">
                <div class="teacher-name">{{ row.item.seccionA.nombreCatedratico }}</div>
                <div class="teacher-details">
                  <span class="teacher-id">{{ row.item.seccionA.idCatedratico }}</span>
                  <span class="teacher-degree"> - {{ row.item.seccionA.gradoAcademico }}</span>
                </div>
              </div>
            </div>
          </template>

          <template #cell(seccionB)="row">
            <div v-if="row.item.seccionB" class="combined-info">
              <div class="course-section">
                <div class="course-code">{{ row.item.seccionB.codigoCurso }}</div>
                <div class="course-name">{{ row.item.seccionB.nombreCurso }}</div>
              </div>
              <div class="teacher-section">
                <div class="teacher-name">{{ row.item.seccionB.nombreCatedratico }}</div>
                <div class="teacher-details">
                  <span class="teacher-id">{{ row.item.seccionB.idCatedratico }}</span>
                  <span class="teacher-degree"> - {{ row.item.seccionB.gradoAcademico }}</span>
                </div>
              </div>
            </div>
          </template>

          <template #cell(seccionC)="row">
            <div v-if="row.item.seccionC" class="combined-info">
              <div class="course-section">
                <div class="course-code">{{ row.item.seccionC.codigoCurso }}</div>
                <div class="course-name">{{ row.item.seccionC.nombreCurso }}</div>
              </div>
              <div class="teacher-section">
                <div class="teacher-name">{{ row.item.seccionC.nombreCatedratico }}</div>
                <div class="teacher-details">
                  <span class="teacher-id">{{ row.item.seccionC.idCatedratico }}</span>
                  <span class="teacher-degree"> - {{ row.item.seccionC.gradoAcademico }}</span>
                </div>
              </div>
            </div>
          </template>
        </b-table>
      </div>

      <!-- Resumen Estadístico -->
      <b-card class="mt-4" v-if="filteredAsignaciones.length > 0">
        <h6 class="mb-3">Resumen del Reporte</h6>
        <b-row>
          <b-col md="3" class="text-center">
            <div class="stat-item">
              <h4 class="text-primary mb-0">{{ totalAsignaciones }}</h4>
              <small class="text-muted">Total Asignaciones</small>
            </div>
          </b-col>
          <b-col md="3" class="text-center">
            <div class="stat-item">
              <h4 class="text-success mb-0">{{ seccionesUnicas.length }}</h4>
              <small class="text-muted">Secciones Activas</small>
            </div>
          </b-col>
          <b-col md="3" class="text-center">
            <div class="stat-item">
              <h4 class="text-info mb-0">{{ cursosUnicos.length }}</h4>
              <small class="text-muted">Cursos Asignados</small>
            </div>
          </b-col>
          <b-col md="3" class="text-center">
            <div class="stat-item">
              <h4 class="text-warning mb-0">{{ catedraticosUnicos.length }}</h4>
              <small class="text-muted">Catedráticos</small>
            </div>
          </b-col>
        </b-row>
      </b-card>

      <!-- Firmas -->
      <div class="signatures-section mt-5 pt-4 border-top">
        <b-row>
          <b-col md="6">
            <div class="signature text-center">
              <div class="signature-line mb-2"></div>
              <p class="mb-1"><strong>Coordinador Académico</strong></p>
              <p class="text-muted">Facultad de Ciencias Económicas</p>
            </div>
          </b-col>
          <b-col md="6">
            <div class="signature text-center">
              <div class="signature-line mb-2"></div>
              <p class="mb-1"><strong>Director de Carrera</strong></p>
              <p class="text-muted">Licenciatura en Administración de Empresas</p>
            </div>
          </b-col>
        </b-row>
      </div>

      <!-- Footer -->
      <div class="footer text-center mt-4">
        <small class="text-muted">Sistema de Gestión Académica - Generado el {{ fechaActual }}</small>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BButton, BSpinner, BTable, BBadge,
  BFormGroup, BFormSelect, BCollapse
} from 'bootstrap-vue'

export default {
  name: 'ReporteAsignacionCursosView',
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BSpinner,
    BTable,
    BBadge,
    BFormGroup,
    BFormSelect,
    BCollapse,
  },
  data() {
    // Definir los encabezados estáticos primero
    const tableFields = [
      { 
        key: 'horario', 
        label: 'HORARIOS', 
        thClass: 'text-center main-header',
        tdClass: 'text-center'
      },
      { 
        key: 'seccionA', 
        label: 'SECCIÓN A', 
        thClass: 'text-center sub-header'
      },
      { 
        key: 'seccionB', 
        label: 'SECCIÓN B', 
        thClass: 'text-center sub-header'
      },
      { 
        key: 'seccionC', 
        label: 'SECCIÓN C', 
        thClass: 'text-center sub-header'
      }
    ]

    return {
      loading: false,
      showExportOptions: false,
      showFilters: false,
      
      // Datos de las asignaciones
      asignaciones: [
        // Horario 07:00-09:00
        {
          id: 1,
          horario: '07:00-09:00',
          seccion: 'A',
          codigoCurso: '5122-345',
          nombreCurso: 'Economía General',
          nombreCatedratico: 'Yuslena Yeray Castañeda Castellanos',
          idCatedratico: '11644',
          gradoAcademico: 'Lic. En Administración de Empresas',
          salon: 'Inter-2A',
          capacidad: 30,
          ciclo: '2025-2'
        },
        {
          id: 2,
          horario: '07:00-09:00',
          seccion: 'B',
          codigoCurso: '5122-346',
          nombreCurso: 'Contabilidad I',
          nombreCatedratico: 'Zoly Alejandra Villalobos Tercero',
          idCatedratico: '17527',
          gradoAcademico: 'Contador Público y Auditor',
          salon: 'Inter-2B',
          capacidad: 30,
          ciclo: '2025-2'
        },
        {
          id: 3,
          horario: '07:00-09:00',
          seccion: 'C',
          codigoCurso: '5122-347',
          nombreCurso: 'Administración II',
          nombreCatedratico: 'Rita Elizabeth Caballeros Polo',
          idCatedratico: '2856',
          gradoAcademico: 'Lic. Administración de Empresas; M.A. R.R.H.H.',
          salon: 'Inter-2C',
          capacidad: 30,
          ciclo: '2025-2'
        },

        // Horario 09:00-11:00
        {
          id: 4,
          horario: '09:00-11:00',
          seccion: 'A',
          codigoCurso: '5122-346',
          nombreCurso: 'Matemática I',
          nombreCatedratico: 'Hector Lionel Barquero',
          idCatedratico: '465',
          gradoAcademico: 'Ingeniero Industrial',
          salon: 'Inter-2A',
          capacidad: 30,
          ciclo: '2025-2'
        },
        {
          id: 5,
          horario: '09:00-11:00',
          seccion: 'B',
          codigoCurso: '5122-349',
          nombreCurso: 'Derecho II',
          nombreCatedratico: 'Marlon Javier Navarro',
          idCatedratico: '16475',
          gradoAcademico: 'Abogada y Notaria',
          salon: 'Inter-2B',
          capacidad: 30,
          ciclo: '2025-2'
        },
        {
          id: 6,
          horario: '09:00-11:00',
          seccion: 'C',
          codigoCurso: '5122-345',
          nombreCurso: 'Economía General',
          nombreCatedratico: 'Yuslena Yeray Castañeda Castellanos',
          idCatedratico: '11644',
          gradoAcademico: 'Lic. En Administración de Empresas',
          salon: 'Inter-2C',
          capacidad: 30,
          ciclo: '2025-2'
        },

        // Horario 11:00-13:00
        {
          id: 7,
          horario: '11:00-13:00',
          seccion: 'A',
          codigoCurso: '5122-347',
          nombreCurso: 'Administración II',
          nombreCatedratico: 'Rita Elizabeth Caballeros Polo',
          idCatedratico: '2856',
          gradoAcademico: 'Lic. Administración de Empresas; M.A. R.R.H.H.',
          salon: 'Inter-2A',
          capacidad: 30,
          ciclo: '2025-2'
        },
        {
          id: 8,
          horario: '11:00-13:00',
          seccion: 'B',
          codigoCurso: '5122-346',
          nombreCurso: 'Matemática I',
          nombreCatedratico: 'Hector Lionel Barquero',
          idCatedratico: '465',
          gradoAcademico: 'Ingeniero Industrial',
          salon: 'Inter-2B',
          capacidad: 30,
          ciclo: '2025-2'
        },
        {
          id: 9,
          horario: '11:00-13:00',
          seccion: 'C',
          codigoCurso: '5122-348',
          nombreCurso: 'Derecho II',
          nombreCatedratico: 'Lorena Elizabeth Montiel Vasco',
          idCatedratico: '1260',
          gradoAcademico: 'Abogada y Notaria',
          salon: 'Inter-2C',
          capacidad: 30,
          ciclo: '2025-2'
        },

        // Horario 14:00-16:00
        {
          id: 10,
          horario: '14:00-16:00',
          seccion: 'A',
          codigoCurso: '5122-348',
          nombreCurso: 'Derecho II',
          nombreCatedratico: 'Marlon Javier Navarro',
          idCatedratico: '16475',
          gradoAcademico: 'Abogada y Notaria',
          salon: 'Inter-2A',
          capacidad: 30,
          ciclo: '2025-2'
        },
        {
          id: 11,
          horario: '14:00-16:00',
          seccion: 'B',
          codigoCurso: '5122-347',
          nombreCurso: 'Administración II',
          nombreCatedratico: 'María Elena Mendez',
          idCatedratico: '4074',
          gradoAcademico: 'Lic. Administración de Empresas; M.A. R.R.H.H.',
          salon: 'Inter-2B',
          capacidad: 30,
          ciclo: '2025-2'
        },
        {
          id: 12,
          horario: '14:00-16:00',
          seccion: 'C',
          codigoCurso: '5122-400',
          nombreCurso: 'Contabilidad I',
          nombreCatedratico: 'Zoly Alejandra Villalobos Tercero',
          idCatedratico: '17527',
          gradoAcademico: 'Contador Público y Auditor',
          salon: 'Inter-2C',
          capacidad: 30,
          ciclo: '2025-2'
        },

        // Horario 16:00-18:00
        {
          id: 13,
          horario: '16:00-18:00',
          seccion: 'A',
          codigoCurso: '5122-400',
          nombreCurso: 'Contabilidad I',
          nombreCatedratico: 'Luisa Maria Contreras Guidel',
          idCatedratico: '12308',
          gradoAcademico: 'Lic. En Administración de Empresas',
          salon: 'Inter-2A',
          capacidad: 30,
          ciclo: '2025-2'
        },
        {
          id: 14,
          horario: '16:00-18:00',
          seccion: 'B',
          codigoCurso: '5122-345',
          nombreCurso: 'Economía General',
          nombreCatedratico: 'Julia Concepción Gil Reynosa',
          idCatedratico: '1578',
          gradoAcademico: 'Lic. En Administración de Empresas',
          salon: 'Inter-2B',
          capacidad: 30,
          ciclo: '2025-2'
        },
        {
          id: 15,
          horario: '16:00-18:00',
          seccion: 'C',
          codigoCurso: '5122-346',
          nombreCurso: 'Matemática I',
          nombreCatedratico: 'Gerardo Castillo Calderón',
          idCatedratico: '4570',
          gradoAcademico: 'Administrador de Empresas',
          salon: 'Inter-2C',
          capacidad: 30,
          ciclo: '2025-2'
        }
      ],

      // Campos de la tabla - ENCABEZADOS ESTÁTICOS INICIALES
      tableFields: tableFields,

      // Filtros
      filters: {
        ciclo: '',
        seccion: '',
        salon: '',
        catedratico: ''
      },

      // Opciones para filtros
      ciclosOptions: [
        { value: '', text: 'Todos los ciclos' },
        { value: '2025-2', text: 'Segundo Semestre 2025' },
        { value: '2025-1', text: 'Primer Semestre 2025' },
        { value: '2024-2', text: 'Segundo Semestre 2024' }
      ],

      seccionesOptions: [
        { value: '', text: 'Todas las secciones' },
        { value: 'A', text: 'Sección A' },
        { value: 'B', text: 'Sección B' },
        { value: 'C', text: 'Sección C' }
      ],

      salonesOptions: [
        { value: '', text: 'Todos los salones' },
        { value: 'Inter-2A', text: 'Inter - 2A' },
        { value: 'Inter-2B', text: 'Inter - 2B' },
        { value: 'Inter-2C', text: 'Inter - 2C' }
      ],

      catedraticosOptions: [
        { value: '', text: 'Todos los catedráticos' },
        { value: 'Yuslena Yeray Castañeda Castellanos', text: 'Yuslena Yeray Castañeda Castellanos' },
        { value: 'Zoly Alejandra Villalobos Tercero', text: 'Zoly Alejandra Villalobos Tercero' },
        { value: 'Rita Elizabeth Caballeros Polo', text: 'Rita Elizabeth Caballeros Polo' },
        { value: 'Hector Lionel Barquero', text: 'Hector Lionel Barquero' },
        { value: 'Marlon Javier Navarro', text: 'Marlon Javier Navarro' }
      ]
    }
  },
  computed: {
    filteredAsignaciones() {
      let filtered = this.asignaciones

      // Filtrar por sección
      if (this.filters.seccion) {
        filtered = filtered.filter(asig => 
          asig.seccion === this.filters.seccion
        )
      }

      // Filtrar por salón
      if (this.filters.salon) {
        filtered = filtered.filter(asig => 
          asig.salon === this.filters.salon
        )
      }

      // Filtrar por catedrático
      if (this.filters.catedratico) {
        filtered = filtered.filter(asig => 
          asig.nombreCatedratico === this.filters.catedratico
        )
      }

      return filtered
    },

    horariosAgrupados() {
      const horarios = ['07:00-09:00', '09:00-11:00', '11:00-13:00', '14:00-16:00', '16:00-18:00']
      
      return horarios.map(horario => {
        const seccionA = this.filteredAsignaciones.find(a => a.horario === horario && a.seccion === 'A')
        const seccionB = this.filteredAsignaciones.find(a => a.horario === horario && a.seccion === 'B')
        const seccionC = this.filteredAsignaciones.find(a => a.horario === horario && a.seccion === 'C')
        
        return {
          horario,
          seccionA,
          seccionB,
          seccionC
        }
      })
    },

    fechaActual() {
      return new Date().toLocaleDateString('es-GT', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },

    totalAsignaciones() {
      return this.filteredAsignaciones.length
    },

    seccionesUnicas() {
      return [...new Set(this.filteredAsignaciones.map(a => a.seccion))]
    },

    cursosUnicos() {
      return [...new Set(this.filteredAsignaciones.map(a => a.nombreCurso))]
    },

    catedraticosUnicos() {
      return [...new Set(this.filteredAsignaciones.map(a => a.nombreCatedratico))]
    },

    // Computed para los encabezados dinámicos
    tableFieldsComputed() {
      return [
        { 
          key: 'horario', 
          label: 'HORARIOS', 
          thClass: 'text-center main-header',
          tdClass: 'text-center'
        },
        { 
          key: 'seccionA', 
          label: this.getSeccionHeader('A'), 
          thClass: 'text-center sub-header'
        },
        { 
          key: 'seccionB', 
          label: this.getSeccionHeader('B'), 
          thClass: 'text-center sub-header'
        },
        { 
          key: 'seccionC', 
          label: this.getSeccionHeader('C'), 
          thClass: 'text-center sub-header'
        }
      ]
    }
  },
  async mounted() {
    await this.loadData()
    // Actualizar los encabezados después de cargar los datos
    this.tableFields = this.tableFieldsComputed
  },
  methods: {
    getSeccionHeader(seccion) {
      const asignacion = this.asignaciones.find(a => a.seccion === seccion)
      if (asignacion) {
        return `Ciclo ${asignacion.ciclo} - Sección ${seccion} - ${asignacion.salon} (Cap. ${asignacion.capacidad})`
      }
      return `SECCIÓN ${seccion}`
    },

    async loadData() {
      this.loading = true
      try {
        // Simular carga de datos desde API
        await new Promise(resolve => setTimeout(resolve, 1500))
        console.log('Datos de asignaciones cargados')
      } catch (error) {
        console.error('Error cargando datos:', error)
        this.$toast({
          title: 'Error',
          text: 'Error al cargar el reporte de asignaciones',
          variant: 'danger'
        })
      } finally {
        this.loading = false
      }
    },

    exportToPDF() {
      // Lógica para exportar a PDF
      console.log('Exportando a PDF...')
      this.$toast({
        title: 'PDF Generado',
        text: 'El reporte se ha exportado correctamente en formato PDF',
        variant: 'success'
      })
    },

    exportToExcel() {
      // Lógica para exportar a Excel
      console.log('Exportando a Excel...')
      this.$toast({
        title: 'Excel Generado',
        text: 'El reporte se ha exportado correctamente en formato Excel',
        variant: 'success'
      })
    },

    clearFilters() {
      this.filters = {
        ciclo: '',
        seccion: '',
        salon: '',
        catedratico: ''
      }
    }
  }
}
</script>

<style scoped>
/* Los estilos se mantienen igual que en la versión anterior */
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 1rem;
  border-bottom: 1px solid #dee2e6;
}

.logo-container {
  width: 150px;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.umg-logo {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.university-name {
  font-size: 1.25rem;
  font-weight: bold;
  color: #2c3e50;
}

.university-center {
  font-size: 1rem;
  color: #6c757d;
}

.career-info {
  background-color: #f8f9fa;
  padding: 1rem;
  border-radius: 8px;
  border-left: 4px solid #3498db;
}

.schedule-grid-table {
  font-size: 0.875rem;
}

.schedule-grid-table td {
  vertical-align: middle;
  padding: 0.5rem;
}

.time-slot {
  text-align: center;
  font-weight: bold;
  color: #2c3e50;
}

.combined-info {
  padding: 0.5rem;
  min-height: 80px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.course-section {
  margin-bottom: 0.5rem;
}

.course-code {
  font-weight: bold;
  color: #2c3e50;
  font-size: 0.9rem;
}

.course-name {
  font-size: 0.8rem;
  color: #6c757d;
  font-style: italic;
}

.teacher-section {
  border-top: 1px solid #e9ecef;
  padding-top: 0.5rem;
}

.teacher-name {
  font-weight: 600;
  color: #2c3e50;
  font-size: 0.85rem;
}

.teacher-details {
  font-size: 0.75rem;
  color: #6c757d;
}

.teacher-id {
  font-weight: 500;
}

.teacher-degree {
  font-style: italic;
}

.signature-line {
  border-top: 1px solid #2c3e50;
  width: 200px;
  margin: 0 auto;
}

.signature p {
  margin-bottom: 0.25rem;
  font-size: 0.875rem;
}

.footer {
  font-size: 0.75rem;
}

.stat-item {
  padding: 1rem;
}

.stat-item h4 {
  font-weight: bold;
}

/* Responsive */
@media (max-width: 768px) {
  .header {
    flex-direction: column;
    text-align: center;
  }
  
  .logo-container {
    margin-bottom: 1rem;
  }
  
  .schedule-grid-table {
    font-size: 0.75rem;
  }
}

.gap-2 > * {
  margin-right: 0.5rem;
  margin-bottom: 0.5rem;
}

::v-deep .schedule-grid-table .table-responsive {
  border: 1px solid #dee2e6;
  border-radius: 8px;
}

::v-deep .schedule-grid-table .table th {
  border-bottom: 2px solid #2c3e50;
  background-color: #f8f9fa !important;
  color: #2c3e50 !important;
  text-align: center;
  vertical-align: middle;
  font-weight: bold;
}

::v-deep .schedule-grid-table .table th.main-header {
  background-color: #e9ecef !important;
  color: #2c3e50 !important;
  font-weight: bold;
  font-size: 1rem;
  border-right: 2px solid #dee2e6;
}

::v-deep .schedule-grid-table .table th.sub-header {
  background-color: #f8f9fa !important;
  color: #2c3e50 !important;
  font-weight: 600;
  font-size: 0.85rem;
  line-height: 1.2;
}

::v-deep .schedule-grid-table .table thead {
  border-bottom: 3px solid #2c3e50;
}

::v-deep .schedule-grid-table .table th:not(:first-child) {
  border-left: 1px solid #dee2e6;
}

::v-deep .schedule-grid-table .table td {
  border-left: 1px solid #dee2e6;
}
</style>